import React, { memo, ReactNodeArray, useCallback, useContext, useMemo, useRef, useState } from 'react'
import { Carousel, StarRating, ScreenContext } from 'react-components'
import { AnyStyledComponent } from 'styled-components'

import EmptyStar from '../../assets/images/deprecated-empty-star.svg'
import FullStar from '../../assets/images/deprecated-full-star.svg'
import HalfStar from '../../assets/images/deprecated-half-star.svg'
import TestimonialCard from './testimonials-card'
import { RichSnippetTransport, TestimonialsSectionTransport } from '@common/transports/deprecated-home-page-transport'
import {
    Section,
    RatingContainer,
    RatingDescription,
    Title,
    LeftArrow,
    RightArrow,
    Dot,
    SliderContainer,
    TestimonialContainer,
    CarouselContainer,
} from './testimonials-styles'
import { SectionContainer } from './common-styles'
import { AnalyticsService } from '@src/services'
import { homePageEventCategories } from '@src/services/analytics-service/categories'
import { homePageEventLabels } from '@src/services/analytics-service/labels'

type Props = {
    testimonialsSectionData: TestimonialsSectionTransport
}

const getCardContent = (testimonialsSectionData: TestimonialsSectionTransport) =>
    testimonialsSectionData &&
    testimonialsSectionData.testimonials.map((testimonial, index) => (
        <TestimonialCard key={`testimonial_${index}`} testimonial={testimonial} />
    ))

const _getRichSnippetHtml = (richSnippet?: RichSnippetTransport) =>
    richSnippet
        ? {
              __html: JSON.stringify({
                  '@context': richSnippet['@context'],
                  '@type': richSnippet['@type'],
                  aggregateRating: richSnippet.aggregateRating
                      ? {
                            '@type': richSnippet.aggregateRating['@type'],
                            ratingValue: richSnippet.aggregateRating.ratingValue,
                            reviewCount: richSnippet.aggregateRating.reviewCount,
                            bestRating: richSnippet.aggregateRating.bestRating,
                            worstRating: richSnippet.aggregateRating.worstRating,
                        }
                      : null,
                  name: richSnippet.name,
              }),
          }
        : null

const _appendDots = (dots: ReactNodeArray) => <SliderContainer>{dots}</SliderContainer>

const _handleSwipe = () => {
    AnalyticsService.trackSwipeEvent(
        homePageEventCategories.TESTIMONIALS_SECTION,
        homePageEventLabels.testimonialsSection.CHANGE_CAROUSEL_SLIDE,
    )
}

const TestimonialSection: React.FC<Props> = ({ testimonialsSectionData }) => {
    const [currentSelection, updateCurrentSelection] = useState<number>(0)
    const cardContent = useMemo(() => getCardContent(testimonialsSectionData), [testimonialsSectionData])

    const _richSnippetHtml = useMemo(
        () => _getRichSnippetHtml(testimonialsSectionData.richSnippet),
        [testimonialsSectionData.richSnippet],
    )

    const _carouselRef = useRef()

    const nextSlide = useCallback(() => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        _carouselRef.current?.slickNext?.()
        AnalyticsService.trackClickEvent(
            homePageEventCategories.TESTIMONIALS_SECTION,
            homePageEventLabels.testimonialsSection.CHANGE_CAROUSEL_SLIDE,
        )
    }, [])

    const prevSlide = useCallback(() => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        _carouselRef.current?.slickPrev?.()
        AnalyticsService.trackClickEvent(
            homePageEventCategories.TESTIMONIALS_SECTION,
            homePageEventLabels.testimonialsSection.CHANGE_CAROUSEL_SLIDE,
        )
    }, [])

    const { isMobile, isTablet, isDesktop } = useContext(ScreenContext)

    const _beforeChange = useCallback(
        (_lastIndex, nextIndex) => {
            updateCurrentSelection(nextIndex)
        },
        [updateCurrentSelection],
    )

    const _customPaging = useCallback(index => <Dot selected={index === currentSelection} />, [currentSelection])

    return (
        <SectionContainer id={testimonialsSectionData.id}>
            <Section>
                <Title>{testimonialsSectionData.title}</Title>
                {!!_richSnippetHtml && <script type="application/ld+json" dangerouslySetInnerHTML={_richSnippetHtml} />}
                <RatingContainer>
                    <StarRating
                        actualRating={testimonialsSectionData.overallRating.actualRating}
                        maxRating={testimonialsSectionData.overallRating.maxRating}
                        customFullStar={FullStar as AnyStyledComponent}
                        customEmptyStar={EmptyStar as AnyStyledComponent}
                        customHalfStar={HalfStar as AnyStyledComponent}
                    />
                </RatingContainer>
                <RatingDescription>{testimonialsSectionData.overallRating.description}</RatingDescription>
                <TestimonialContainer>
                    {isDesktop && <LeftArrow onClick={prevSlide} />}
                    <CarouselContainer>
                        <Carousel
                            arrows={false}
                            infinite
                            centerMode
                            ref={_carouselRef}
                            centerPadding={isMobile ? '16px' : isTablet ? '28px' : '0'}
                            customPaging={_customPaging}
                            appendDots={_appendDots}
                            beforeChange={_beforeChange}
                            onSwipe={_handleSwipe}
                            touchThreshold={8}
                            dots
                        >
                            {cardContent}
                        </Carousel>
                    </CarouselContainer>
                    {isDesktop && <RightArrow onClick={nextSlide} />}
                </TestimonialContainer>
            </Section>
        </SectionContainer>
    )
}

export default memo(TestimonialSection)
